import styled from "@emotion/styled";
import * as React from "react";

import { CheckCircle } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Icon,
  ShadowToken,
  SpacingToken,
  Tooltip,
} from "@livechat/design-system-react-components";

import { Collapse } from "components/collapse";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { DESKTOP_NAVIGATION_WIDTH } from "components/navigation/NavigationDesktop";
import { isInIframe } from "helpers/app";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

export enum SaveBarMode {
  Hidden = "hidden",
  OfferSave = "save",
  Saving = "saving",
  Saved = "saved",
}

interface IProps {
  onSave(): void;
  onCancel(): void;
  className?: string;
  saveDisabled?: boolean;
  saveTooltip?: string;
  mode?: SaveBarMode;
  cancelLabel?: string;
  customSaveLabel?: string;
}

export const SaveBar: React.FC<IProps> = props => {
  let saveLabel: string;
  if (props.mode === SaveBarMode.OfferSave) {
    saveLabel = props.customSaveLabel || "Save changes";
  } else if (props.mode === SaveBarMode.Saving) {
    saveLabel = "Saving…";
  }
  const isEmbeddedInIframe = isInIframe();
  const saveDisabled = props.saveDisabled || props.mode === SaveBarMode.Saving;
  const [isTooltipHovered, setIsTooltipHovered] = React.useState(false);
  const isSaveTooltipVisible =
    saveDisabled && isTooltipHovered && Boolean(props.saveTooltip && props.saveTooltip.length > 0);

  return (
    <OuterWrapper className={props.className}>
      <Collapse collapsed={props.mode === SaveBarMode.Hidden}>
        <StyledWrapper>
          {(props.mode === SaveBarMode.OfferSave || props.mode === SaveBarMode.Saving) && (
            <Flex gap={withVar(SpacingToken.Spacing5)}>
              <Button
                disabled={props.mode === SaveBarMode.Saving}
                onClick={props.onCancel}
                data-testid="save-bar-cancel-button"
                kind="secondary"
                size="large"
              >
                {props.cancelLabel}
              </Button>
              <Tooltip
                isVisible={isSaveTooltipVisible}
                kind="invert"
                triggerRenderer={() => (
                  <div onMouseEnter={() => setIsTooltipHovered(true)} onMouseLeave={() => setIsTooltipHovered(false)}>
                    <Button
                      kind={isEmbeddedInIframe ? "high-contrast" : "primary"}
                      size="large"
                      onClick={props.onSave}
                      disabled={saveDisabled}
                      data-testid="save-bar-save-button"
                    >
                      {saveLabel}
                    </Button>
                  </div>
                )}
              >
                <div>{props.saveTooltip}</div>
              </Tooltip>
            </Flex>
          )}
          {props.mode === SaveBarMode.Saved && (
            <>
              <StyledIconContainer>
                <Icon source={CheckCircle} />
              </StyledIconContainer>
              Changes saved!
            </>
          )}
        </StyledWrapper>
      </Collapse>
    </OuterWrapper>
  );
};

SaveBar.defaultProps = {
  cancelLabel: "Cancel",
};

export const FixedSaveBar = styled(SaveBar)<{ embeddedInIframe?: boolean }>`
  position: fixed;
  bottom: 0;
  left: ${(props): number => (props.embeddedInIframe ? 0 : DESKTOP_NAVIGATION_WIDTH)}px;
  right: ${(props): number => (props.embeddedInIframe ? 0 : 6)}px;
  z-index: ${StackingContextLevel.ModalOverlay};
  opacity: 1;

  @media (max-width: ${(props): string => props.theme.mobileBreakpoint}) {
    left: 6px;
    bottom: 60px;
  }
`;

const OuterWrapper = styled.div`
  z-index: 1;
  box-shadow: ${withVar(ShadowToken.PopOver)};
  transition: box-shadow 0.3s ease;
`;

/* implicit min-height is required for <Collapse> component to work properly */
/* (its children heights are calculated before children get actual height) */
export const SAVE_BAR_MIN_HEIGHT = 100;
const StyledWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 29px 0;
  color: var(${DesignToken.ContentBasicPrimary});
  background-color: var(${DesignToken.SurfacePrimaryDefault});
  box-shadow: var(${ShadowToken.FixedTop});

  min-height: ${SAVE_BAR_MIN_HEIGHT}px;
  box-sizing: border-box;
`;

const StyledIconContainer = styled.span`
  margin-right: 10px;
`;

export const SaveBarSpaceEquivalent = (): React.ReactNode => <Block height={`${SAVE_BAR_MIN_HEIGHT}px`} />;
